import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { queueSettingsQueryOptions, updateQueueSettingsFn, resetQueueSettingsFn } from "@/server/queues";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAppForm } from "@/components/form/form-context";
import { settingTypesQueryOptions, GroupedSettingTypeResponse, systemSettingsQueryOptions } from "@/server/settings";
import { SettingListResponse, SettingType } from "@/lib/mma/types.gen";
import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { SettingTab } from "../../organisations/$orgId/settings/-setting-tab";
import { ResponsiveActionBar, ResponsiveFormSection } from "@/components/form/responsive-form";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Filter } from "@/routes/_dashboard/settings/-components/settings-toolbar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ConfirmDialog } from "@/components/confirm-dialog";
import {
  calculateResetValues,
  hasUpdatePermission,
  getPermissionRestraintMessage,
  isForbiddenError,
  ContextUser,
} from "@/lib/utils";
import { UnsavedChangesGuard } from "@/hooks/unsaved-changes-guard";
import { usePermissionRedirect } from "@/hooks/use-permission-redirect";

export const Route = createFileRoute("/_dashboard/queues/$queueId/settings")({
  component: RouteComponent,
  beforeLoad: () => {
    return { title: "Settings" };
  },
  loader: async ({ context }) => {
    return {
      user: context.user as ContextUser | null | undefined,
      crumb: "Settings",
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const loaderData = Route.useLoaderData();
  const user = loaderData.user as ContextUser | null;
  usePermissionRedirect({
    user,
    permission: "SETTINGS",
    toastMessage: "You don't have permission to view queue settings.",
    redirectTo: "/queues",
  });
  const params = Route.useParams();
  const queueSettingsQuery = useQuery(queueSettingsQueryOptions(Number(params.queueId)));
  const queueSettings = queueSettingsQuery.data;
  const settingTypesQuery = useQuery(settingTypesQueryOptions);
  const settingTypes = settingTypesQuery.data as GroupedSettingTypeResponse;
  const systemSettingsQuery = useQuery(systemSettingsQueryOptions);
  const systemSettings = systemSettingsQuery.data as SettingListResponse;
  const queryClient = useQueryClient();
  const [processedChoiceTypes, setProcessedChoiceTypes] = useState<Set<string>>(new Set());
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveAffectedItems, setSaveAffectedItems] = useState<string[]>([]);
  const [pendingPayload, setPendingPayload] = useState<Record<string, any> | null>(null);
  const searchParams = Route.useSearch() as {
    search?: string;
    tab?: string;
    overwrittenOnly?: string;
  };

  const activeTab = searchParams.tab ?? "all";
  const search = searchParams.search ?? "";
  const overwrittenOnly = searchParams.overwrittenOnly === "true";

  useEffect(() => {
    if (settingTypesQuery.isError) {
      if (isForbiddenError(settingTypesQuery.error) && user) {
        toast.error(getPermissionRestraintMessage("view queue settings"));
        const nextRoute = "/queues";
        const timer = setTimeout(() => {
          navigate({ to: nextRoute });
        }, 500);
        return () => clearTimeout(timer);
      }
      toast.error("Failed to load settings. Please try again.");
      return;
    }
    if (systemSettingsQuery.isError) {
      toast.error("Failed to load settings configuration. Please try again.");
      return;
    }
  }, [settingTypesQuery.isError, settingTypesQuery.error, systemSettingsQuery.isError, user, navigate]);

  if (settingTypesQuery.isError && isForbiddenError(settingTypesQuery.error)) {
    return null;
  }

  if (systemSettingsQuery.isError || settingTypesQuery.isError || !settingTypes?.data) {
    return (
      <ResponsiveFormSection className="flex flex-col gap-8">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-destructive font-semibold">Failed to load settings</p>
              <p className="text-sm text-muted-foreground mt-2">
                Failed to load settings configuration. Please try again.
              </p>
            </div>
          </CardContent>
        </Card>
      </ResponsiveFormSection>
    );
  }

  if (settingTypesQuery.isLoading || systemSettingsQuery.isLoading) {
    return (
      <ResponsiveFormSection className="flex flex-col gap-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-center items-center">
              <Spinner />
              <span className="ml-2">Loading settings...</span>
            </div>
          </CardContent>
        </Card>
      </ResponsiveFormSection>
    );
  }
  useEffect(() => {
    if (!searchParams.tab) {
      navigate({
        to: "/queues/$queueId/settings",
        params,
        replace: true,
        search: (prev) => ({
          ...prev,
          tab: "all",
        }),
      });
    }
  }, [searchParams.tab, navigate, params]);

  // Debounce the URL search param to avoid filtering on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [isSearching, setIsSearching] = useState(false);
  useEffect(() => {
    setDebouncedSearch(search);
    setIsSearching(false);
  }, [search, overwrittenOnly]);

  //Wrapping heavy functions in useRef to avoid re-rendering on tab change since everything rerenders when URL changes.
  const queueSettingTypesRef = useRef<{
    value: any[];
    dependency: any;
  }>({ value: [], dependency: null });

  if (queueSettingTypesRef.current.dependency !== settingTypes.data) {
    queueSettingTypesRef.current = {
      value:
        settingTypes.data
          ?.map((group) => ({
            ...group,
            settings: group.settings?.filter((setting) => setting.domain === "QUEUE") as unknown as (SettingType & {
              name: string;
            })[],
          }))
          .filter((group) => group.settings?.length > 0)
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)) || [],
      dependency: settingTypes.data,
    };
  }

  const queueSettingTypes = queueSettingTypesRef.current.value;

  const defaultSystemValuesRef = useRef<{
    value: Record<string, any>;
    queueSettingTypes: any[];
    systemSettings: any;
  }>({
    value: {},
    queueSettingTypes: [],
    systemSettings: null,
  });

  if (
    defaultSystemValuesRef.current.queueSettingTypes !== queueSettingTypes ||
    defaultSystemValuesRef.current.systemSettings !== systemSettings
  ) {
    defaultSystemValuesRef.current = {
      value: Object.fromEntries(
        queueSettingTypes
          .flatMap((group) => group.settings)
          .filter((setting) => Object.hasOwn(systemSettings, setting.name))
          .map((setting) => [setting.name, systemSettings[setting.name]])
      ),
      queueSettingTypes,
      systemSettings,
    };
  }

  const defaultSystemValues = defaultSystemValuesRef.current.value;

  const overwrittenSettingNames = useMemo(() => {
    if (!queueSettings) return new Set<string>();
    return new Set(Object.keys(queueSettings));
  }, [queueSettings]);

  const filteredSettings = useMemo(() => {
    const lowerSearch = debouncedSearch.toLowerCase();
    return (queueSettingTypes || [])
      .map((group) => ({
        ...group,
        settings: group.settings?.filter(
          (setting) =>
            (setting.displayName.toLowerCase().includes(lowerSearch) ||
              setting.name.toLowerCase().includes(lowerSearch) ||
              (setting.description?.toLowerCase().includes(lowerSearch) ?? false)) &&
            (!overwrittenOnly || overwrittenSettingNames.has(setting.name))
        ),
      }))
      .filter((group) => (group.settings?.length ?? 0) > 0);
  }, [queueSettingTypes, debouncedSearch, overwrittenOnly, overwrittenSettingNames]);

  useEffect(() => {
    setIsSearching(false);
  }, [filteredSettings]);

  useEffect(() => {
    if (!queueSettingTypes) return;

    const uniqueChoiceTypes = new Set<string>();

    queueSettingTypes.forEach((settingGroup: any) => {
      settingGroup.settings.forEach((setting: any) => {
        if (
          setting?.validation?.type === "choices" &&
          !setting?.validation?.choices?.predefinedChoices?.length &&
          typeof setting?.validation?.choices?.choiceType === "string" &&
          setting.validation.choices.choiceType &&
          setting?.validation?.choices?.choiceType !== "predefined"
        ) {
          uniqueChoiceTypes.add(setting.validation.choices.choiceType as string);
        }
      });
    });

    if (uniqueChoiceTypes.size === 0) return;

    // Load choices for each type that hasn't been processed yet.
    uniqueChoiceTypes.forEach((choiceType) => {
      if (!processedChoiceTypes.has(choiceType)) {
        setProcessedChoiceTypes((prev) => new Set(prev).add(choiceType));
      }
    });
  }, [settingTypes]);

  const updateQueueSettingsMutation = useMutation({
    mutationFn: updateQueueSettingsFn,
    onSuccess: () => {
      setSaveDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["queue-settings", Number(params.queueId)] });
      queryClient.invalidateQueries({ queryKey: ["queue", Number(params.queueId)] });
      setSaveDialogOpen(false);
    },
    onError: (error) => {
      // Close dialog on permission errors
      if (error instanceof Error && error.message.includes("not permitted")) {
        setSaveDialogOpen(false);
      }
    },
  });

  const resetQueueSettingsMutation = useMutation({
    mutationFn: resetQueueSettingsFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["queue-settings", Number(params.queueId)] });
      setResetDialogOpen(false);
    },
    onError: (error) => {
      // Close dialog on permission errors
      if (error instanceof Error && error.message.includes("not permitted")) {
        setResetDialogOpen(false);
      }
    },
  });

  const initiateFormValues = () => {
    return {
      ...defaultSystemValues,
      ...queueSettings,
    } as any;
  };

  const form = useAppForm({
    defaultValues: initiateFormValues(),
    onSubmit: async (formData) => {
      const defaultValues = initiateFormValues();
      const formValues = formData.value;
      const settingBody: Record<string, any> = {};
      Object.keys(defaultValues).forEach((key) => {
        if (defaultValues[key] !== formValues[key]) {
          settingBody[key] = formValues[key];
        }
      });

      const changedKeys = Object.keys(settingBody);
      const items: string[] = [];
      queueSettingTypes.forEach((group) => {
        group.settings?.forEach((setting: any) => {
          if (changedKeys.includes(setting.name)) {
            items.push(setting.displayName);
          }
        });
      });

      setSaveAffectedItems(items);
      setPendingPayload(settingBody);
      setSaveDialogOpen(true);
    },
  }) as any;

  const updateQueueSettings = async (formValues: any) => {
    const updateQueueSettingsPromise = updateQueueSettingsMutation.mutateAsync({
      data: {
        queueId: Number(params.queueId),
        body: formValues,
      },
    });
    toast.promise(updateQueueSettingsPromise, {
      loading: "Updating settings...",
      success: "Settings updated successfully",
      error: (error) => error.message || "Failed to update settings. Please try again.",
    });
  };

  // Re-initialize form values whenever the active queue's settings have (re)loaded
  useEffect(() => {
    if (!queueSettingsQuery.isSuccess) return;
    form.reset(initiateFormValues());
  }, [params.queueId, queueSettingsQuery.dataUpdatedAt]);

  const handleTabChange = (value: string) => {
    navigate({
      to: "/queues/$queueId/settings",
      params,
      replace: true,
      search: (prev) => ({
        ...prev,
        tab: value,
      }),
    });
  };

  const getTabName = (displayName: string) => {
    return displayName.toLowerCase().replace(/ /g, "-");
  };

  // Auto-switch to first available tab when current tab has no filtered results
  useEffect(() => {
    if (filteredSettings.length === 0) return;

    const currentTabName = activeTab;
    const availableTabNames = ["all", ...filteredSettings.map((group) => getTabName(group.displayName ?? ""))];
    const isCurrentTabAvailable = availableTabNames.includes(currentTabName);

    if (!isCurrentTabAvailable && availableTabNames.length > 0) {
      const firstAvailableTab = availableTabNames[0];
      navigate({
        to: "/queues/$queueId/settings",
        params,
        replace: true,
        search: (prev) => ({
          ...prev,
          tab: firstAvailableTab,
        }),
      });
    }
  }, [filteredSettings, activeTab, navigate, params]);

  const handleFilterChange = useCallback(
    (filters: { search?: string; tab?: string; overwrittenOnly?: boolean }) => {
      navigate({
        to: "/queues/$queueId/settings",
        params: { queueId: params.queueId },
        replace: true,
        search: (prev) => {
          const newSearch = { ...prev };

          // Properly handle search param: set it if present, delete if cleared
          if (filters.search?.trim()) {
            newSearch.search = filters.search.trim();
          } else {
            delete newSearch.search;
          }

          // Handle tab param
          if (filters.tab !== undefined) {
            newSearch.tab = filters.tab;
          }

          // Handle overwrittenOnly param
          if (filters.overwrittenOnly !== undefined) {
            if (filters.overwrittenOnly) {
              newSearch.overwrittenOnly = "true";
            } else {
              delete newSearch.overwrittenOnly;
            }
          }

          // Only show loading state if search params actually changed
          const prevSearch = JSON.stringify(prev);
          const nextSearch = JSON.stringify(newSearch);
          if (prevSearch !== nextSearch) {
            setIsSearching(true);
          }

          return newSearch;
        },
      });
    },
    [navigate, params.queueId]
  );

  const resetToDefault = async () => {
    if (!hasUpdatePermission(user, "QUEUE") || !hasUpdatePermission(user, "SETTINGS")) {
      toast.error(getPermissionRestraintMessage("reset queue settings"));
      setResetDialogOpen(false);
      return;
    }

    try {
      const resetQueueSettingsPromise = resetQueueSettingsMutation.mutateAsync({
        data: { queueId: Number(params.queueId) },
      });
      toast.promise(resetQueueSettingsPromise, {
        loading: "Resetting to default...",
        success: () => {
          return "Settings reset to default";
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <ResponsiveFormSection>
        <form.Subscribe
          selector={(state: any) => state.isDirty}
          children={(isDirty: boolean) => <UnsavedChangesGuard isDirty={isDirty} />}
        />
        <div className="mb-4">
          <Filter
            onFilterChange={handleFilterChange}
            defaultSearch={search}
            items={queueSettingTypes}
            overwrittenOnly={overwrittenOnly}
            showOverwrittenFilter={true}
          />
        </div>
        {isSearching && (
          <div className="absolute top-0 left-0 w-full h-full bg-background/80 z-10 flex justify-center">
            <Badge className="h-10 mt-36" variant="secondary">
              <Spinner />
              Searching...
            </Badge>
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            {filteredSettings.length > 0 && (
              <TabsList className="w-full flex flex-wrap mb-7">
                <TabsTrigger value="all" key="all">
                  All
                </TabsTrigger>
                {filteredSettings.map((group) => (
                  <TabsTrigger value={getTabName(group.displayName ?? "")} key={group.groupId}>
                    {group.displayName ?? ""}
                  </TabsTrigger>
                ))}
              </TabsList>
            )}
            {filteredSettings.length > 0 && (
              <TabsContent value="all" className="mb-5">
                <SettingTab
                  searchTerm={debouncedSearch}
                  form={form}
                  settings={filteredSettings.flatMap((group) => group.settings ?? [])}
                  title="All"
                  processedChoiceTypes={processedChoiceTypes}
                />
              </TabsContent>
            )}
            {filteredSettings.map((settingType) => (
              <TabsContent value={getTabName(settingType.displayName ?? "")} key={settingType.groupId} className="mb-4">
                <SettingTab
                  searchTerm={debouncedSearch}
                  form={form}
                  settings={settingType.settings ?? []}
                  title={settingType.displayName ?? ""}
                  processedChoiceTypes={processedChoiceTypes}
                />
              </TabsContent>
            ))}
            {filteredSettings.length === 0 && (
              <Card className="mb-5">
                <CardContent>
                  <p className="text-md pb-20 pt-15 text-center">
                    {debouncedSearch ? "No results found." : "No overwritten settings found."}
                  </p>
                </CardContent>
              </Card>
            )}
          </Tabs>

          <form.Subscribe selector={(s) => [s.isSubmitting, s.isDirty, s.canSubmit, s.values]}>
            {([isSubmitting, isDirty, canSubmit, values]) => {
              const defaultVals = initiateFormValues();
              const hasChanges = Object.keys(defaultVals).some((key) => (values as any)[key] !== defaultVals[key]);
              const mayUpdate = hasUpdatePermission(user, "QUEUE") && hasUpdatePermission(user, "SETTINGS");
              return (
                <ResponsiveActionBar>
                  <div className="flex gap-2">
                    {mayUpdate && (
                      <Button
                        type="submit"
                        disabled={isSubmitting || !hasChanges || !canSubmit || updateQueueSettingsMutation.isPending}
                      >
                        {isSubmitting || updateQueueSettingsMutation.isPending ? "Saving..." : "Save"}
                      </Button>
                    )}
                    {mayUpdate && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={calculateResetValues(queueSettings, queueSettingTypes).length === 0}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setResetDialogOpen(true);
                            }}
                          >
                            Reset to default
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Reset all settings to their default values.</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        form.reset(initiateFormValues());
                      }}
                      disabled={isSubmitting || !hasChanges || !canSubmit || updateQueueSettingsMutation.isPending}
                    >
                      Discard
                    </Button>
                  </div>
                </ResponsiveActionBar>
              );
            }}
          </form.Subscribe>
        </form>
      </ResponsiveFormSection>

      <ConfirmDialog
        open={resetDialogOpen}
        title="Reset to default"
        description="Are you sure you want to reset all settings to their default values? This action cannot be undone."
        subtitle="The following settings will be reset to their default values:"
        affectedItems={calculateResetValues(queueSettings, queueSettingTypes)}
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={resetToDefault}
        onCancel={() => {
          setResetDialogOpen(false);
        }}
        loading={resetQueueSettingsMutation.isPending}
      />
      <ConfirmDialog
        open={saveDialogOpen}
        title="Save settings"
        description="Are you sure you want to save the following changes?"
        affectedItems={saveAffectedItems}
        confirmLabel="Save"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (pendingPayload) {
            updateQueueSettings(pendingPayload);
          }
        }}
        onCancel={() => {
          setSaveDialogOpen(false);
        }}
        confirmVariant="default"
        loadingLabel="Saving..."
        loading={updateQueueSettingsMutation.isPending}
      />
    </>
  );
}
